import { ReactNode } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import Button from './Button'
import { useTranslation } from '../../core/hooks/useTranslation'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info' | 'primary'
  icon?: ReactNode
  children?: ReactNode
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  icon,
  children
}: ConfirmationModalProps) {
  const { isRTL } = useTranslation()
  
  if (!isOpen) return null

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-semibold shadow-lg hover:shadow-red-200 transition-all duration-200 border-2 border-red-500 hover:border-red-600'
        }
      case 'warning':
        return {
          iconBg: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          confirmButton: 'bg-yellow-600 hover:bg-yellow-700 text-white font-semibold shadow-lg hover:shadow-yellow-200 transition-all duration-200 border-2 border-yellow-500 hover:border-yellow-600'
        }
      case 'info':
        return {
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg hover:shadow-blue-200 transition-all duration-200 border-2 border-blue-500 hover:border-blue-600'
        }
      case 'primary':
        return {
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg hover:shadow-blue-200 transition-all duration-200 border-2 border-blue-500 hover:border-blue-600'
        }
      default:
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-semibold shadow-lg hover:shadow-red-200 transition-all duration-200 border-2 border-red-500 hover:border-red-600'
        }
    }
  }

  const styles = getVariantStyles()

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 relative"
        style={{
          maxHeight: '90vh',
          overflow: 'auto',
          transform: 'translateZ(0)',
          willChange: 'transform'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between sm:p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${styles.iconBg}`}>
              {icon || <AlertTriangle className={`w-5 h-5 ${styles.iconColor}`} />}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-base text-gray-600 leading-relaxed">{message}</p>
          {children}
        </div>

         {/* Actions */}
         <div className="flex gap-4 p-4 border-t border-gray-200">
           <Button
             variant={variant === 'primary' ? 'primary' : 'danger'}
             size="md"
             onClick={onConfirm}
             className={`px-${isRTL ? '6' : '8'}`}
           >
             {confirmText}
           </Button>
           <Button
             variant="outline"
             size="md"
             onClick={onClose}
             className="px-8"
           >
             {cancelText}
           </Button>
         </div>
      </div>
    </div>
  )
}
